import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { Menu, X, LayoutDashboard, LogOut } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/near-me", label: "Find Help" },
  { to: "/help", label: "Donate" },
  { to: "/for-grocery-stores", label: "For Grocery Stores" },
  { to: "/for-aid-organizations", label: "For Aid Organizations" },
  { to: "/transparency", label: "Transparency" },
  { to: "/about", label: "About" },
];

export function PublicNav() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const loggedIn = !!user;
  const dashHref = user?.role === "grocery_store" ? "/store" : user?.role === "aid_organization" ? "/organization" : "/admin";

  return (
    <header className="fixed top-0 inset-x-0 z-[1000] backdrop-blur-xl bg-white/75 border-b border-stone-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo"><Logo /></Link>

        <nav className="hidden lg:flex items-center gap-6 text-sm font-medium">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `transition-colors ${isActive ? "text-emerald-800" : "text-stone-600 hover:text-emerald-800"}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {loggedIn ? (
            <>
              <Link data-testid="nav-dashboard-link" to={dashHref} className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium bg-emerald-800 text-white rounded-full px-5 py-2.5 hover:bg-emerald-900 transition-all hover:-translate-y-0.5">
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              <button data-testid="nav-logout-btn" onClick={logout} title="Log out" className="hidden sm:inline-flex p-2 rounded-full hover:bg-stone-100 text-stone-700">
                <LogOut className="w-5 h-5" />
              </button>
            </>
          ) : (
            <>
              <Link data-testid="nav-login" to="/login" title="Log in is only for grocery stores & aid organizations" className="hidden sm:inline text-sm font-medium text-stone-700 hover:text-emerald-800 transition-colors">
                Log in <span className="hidden xl:inline text-stone-400 font-normal">(orgs & stores)</span>
              </Link>
              <Link data-testid="nav-register" to="/register" className="hidden sm:inline text-sm font-medium bg-emerald-800 text-white rounded-full px-5 py-2.5 hover:bg-emerald-900 transition-all hover:-translate-y-0.5">
                Get started
              </Link>
            </>
          )}
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen((o) => !o)} className="lg:hidden p-2 rounded-full hover:bg-stone-100">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-stone-200 bg-white px-6 py-4 space-y-3">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)} className={({ isActive }) => `block text-sm font-medium ${isActive ? "text-emerald-800" : "text-stone-700"}`}>
              {l.label}
            </NavLink>
          ))}
          <div className="flex gap-3 pt-2">
            {loggedIn ? (
              <>
                <Link to={dashHref} onClick={() => setOpen(false)} className="text-sm font-medium bg-emerald-800 text-white rounded-full px-5 py-2">Dashboard</Link>
                <button onClick={() => { setOpen(false); logout(); }} className="text-sm font-medium text-stone-700">Log out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-medium text-stone-700">Log in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="text-sm font-medium bg-emerald-800 text-white rounded-full px-5 py-2">Get started</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
